// Cashier shifts: what the drawer should hold and the cash-up variance
// (docs/plans/phase1-billing.md §5.4; cashier-and-payments.md §1 and §5).
//
// Only cash is counted. Other methods are shown as expected totals so the cashier and the
// supervisor can check them against the paybill statement or card slips; reconciling them
// is the M-Pesa slice's job (plan §13 decision 12).

import type { Money } from '../money.ts';
import { addMoney, subtractMoney, zeroMoney } from '../money.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const PAYMENT_METHODS = ['cash', 'mpesa', 'card', 'bank_transfer'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const REFUND_METHODS = ['cash', 'mpesa_reversal', 'bank_transfer'] as const;
export type RefundMethod = (typeof REFUND_METHODS)[number];

/** The payment method a refund method takes money back from. */
export const REFUND_METHOD_SOURCE: Record<RefundMethod, PaymentMethod> = {
  cash: 'cash',
  mpesa_reversal: 'mpesa',
  bank_transfer: 'bank_transfer',
};

export const PAYMENT_STATUSES = ['pending', 'completed', 'failed'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export interface ShiftPayment {
  readonly method: PaymentMethod;
  readonly amount: Money;
  readonly status: PaymentStatus;
}

export interface ShiftRefund {
  readonly method: RefundMethod;
  readonly amount: Money;
}

export interface ShiftTotals {
  /** Completed payments received, by method. */
  readonly received: Record<PaymentMethod, Money>;
  /** Refunds paid out during the shift, by the method they came out of. */
  readonly refunded: Record<PaymentMethod, Money>;
  /** received − refunded, by method. */
  readonly net: Record<PaymentMethod, Money>;
  readonly openingFloat: Money;
  /** Opening float + cash received − cash refunded: what the drawer should hold. */
  readonly expectedCash: Money;
}

function byMethod(): Record<PaymentMethod, Money> {
  return { cash: zeroMoney(), mpesa: zeroMoney(), card: zeroMoney(), bank_transfer: zeroMoney() };
}

/** Pending and failed payments are not money and never count. */
export function shiftTotals(input: {
  readonly openingFloat: Money;
  readonly payments: readonly ShiftPayment[];
  readonly refundsPaid: readonly ShiftRefund[];
}): ShiftTotals {
  if (input.openingFloat.cents < 0n) throw new RangeError('An opening float cannot be negative');
  const received = byMethod();
  const refunded = byMethod();
  for (const payment of input.payments) {
    if (payment.amount.cents <= 0n) throw new RangeError('A payment amount must be positive');
    if (payment.status !== 'completed') continue;
    received[payment.method] = addMoney(received[payment.method], payment.amount);
  }
  for (const refund of input.refundsPaid) {
    if (refund.amount.cents <= 0n) throw new RangeError('A refund amount must be positive');
    const method = REFUND_METHOD_SOURCE[refund.method];
    refunded[method] = addMoney(refunded[method], refund.amount);
  }
  const net = byMethod();
  for (const method of PAYMENT_METHODS)
    net[method] = subtractMoney(received[method], refunded[method]);
  return {
    received,
    refunded,
    net,
    openingFloat: input.openingFloat,
    expectedCash: addMoney(input.openingFloat, net.cash),
  };
}

export type CashUpErrorCode = 'shift.counted_negative' | 'shift.variance_reason_required';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const CASH_UP_ERRORS: Record<CashUpErrorCode, string> = {
  'shift.counted_negative': 'The cash counted cannot be less than zero',
  'shift.variance_reason_required':
    'The cash counted does not match what is expected. Say why before closing the shift',
};

export type VarianceDirection = 'balanced' | 'over' | 'short';

export interface CashUp {
  readonly expected: Money;
  readonly counted: Money;
  /** counted − expected: positive is over, negative is short. */
  readonly variance: Money;
  readonly direction: VarianceDirection;
}

/**
 * Closing count. A variance is never blocked, but it has to be explained
 * (cashier-and-payments.md §5.2).
 */
export function cashUp(input: {
  readonly expected: Money;
  readonly counted: Money;
  readonly reason?: string | null | undefined;
}): Result<CashUp, CashUpErrorCode> {
  if (input.counted.cents < 0n) return err('shift.counted_negative');
  const variance = subtractMoney(input.counted, input.expected);
  const direction: VarianceDirection =
    variance.cents === 0n ? 'balanced' : variance.cents > 0n ? 'over' : 'short';
  if (direction !== 'balanced' && (input.reason ?? '').trim() === '')
    return err('shift.variance_reason_required');
  return ok({ expected: input.expected, counted: input.counted, variance, direction });
}
